import { TwitchStreamsService, type Stream } from '../lib/twitchStreams';
import { twitchService } from '../lib/twitch';
import { raidQueueService } from '../lib/raidQueue';
import { r3LabsService } from '../lib/r3dlabs';
import { formatDuration, formatViewerCount } from '../utils/formatters';
import Swal from 'sweetalert2';
import './TeammatesComponent.css';

// Extended Stream interface to track source
interface ExtendedStream extends Stream {
  teams?: Array<{
    team_id: string;
    team_name: string;
  }>;
}

export class TeammatesComponent {
  private button!: HTMLElement;
  private badge!: HTMLElement;
  private popout!: HTMLElement;
  private teammatesList!: HTMLElement;
  private closeButton!: HTMLElement;
  private teammateStreams: ExtendedStream[] = [];
  private streamsService!: TwitchStreamsService;
  private refreshInterval: number | null = null;
  private readonly REFRESH_INTERVAL = 2 * 60 * 1000; // 2 minutes
  private isVisible = false;

  constructor() {
    // Don't initialize TwitchStreamsService here - wait for initialize method
  }

  private initializeElements(): void {
    // Find the button and badge elements
    this.button = document.querySelector('.teammates-button') as HTMLElement;
    this.badge = document.querySelector('.teammates-badge') as HTMLElement;
    this.popout = document.querySelector('.teammates-popout') as HTMLElement;
    this.teammatesList = this.popout?.querySelector('.teammates-list') as HTMLElement;
    this.closeButton = this.popout?.querySelector('.teammates-popout-close') as HTMLElement;

    // Check each element individually and provide specific error messages
    const missingElements = [];
    if (!this.button) missingElements.push('teammates-button');
    if (!this.badge) missingElements.push('teammates-badge');
    if (!this.popout) missingElements.push('teammates-popout');
    if (!this.teammatesList) missingElements.push('teammates-list');
    if (!this.closeButton) missingElements.push('teammates-popout-close');

    if (missingElements.length > 0) {
      throw new Error(`Required teammates elements not found: ${missingElements.join(', ')}`);
    }

    // Initialize badge to hidden state
    this.badge.style.display = 'none';
  }

  private initializeEventListeners(): void {
    // Button click to toggle popout
    this.button.addEventListener('click', (e) => {
      e.stopPropagation();
      this.togglePopout();
    });

    // Close button
    this.closeButton.addEventListener('click', () => {
      this.hidePopout();
    });

    // Click outside to close
    document.addEventListener('click', (e) => {
      if (this.isVisible && !this.popout.contains(e.target as Node) && !this.button.contains(e.target as Node)) {
        this.hidePopout();
      }
    });

    // Teammates list click handlers
    this.teammatesList.addEventListener('click', this.handleTeammateClick.bind(this));
  }

  private togglePopout(): void {
    if (this.isVisible) {
      this.hidePopout();
    } else {
      this.showPopout();
    }
  }

  private showPopout(): void {
    // Close raid queue popout if it's open
    const raidQueuePopout = document.querySelector('.raid-queue-popout');
    if (raidQueuePopout && raidQueuePopout.classList.contains('active')) {
      raidQueuePopout.classList.remove('active');
    }
    
    this.isVisible = true;
    this.popout.classList.add('active');
    this.updateTeammates(); // Refresh data when opening
  }

  private hidePopout(): void {
    this.isVisible = false;
    this.popout.classList.remove('active');
  }

  private async handleTeammateClick(e: MouseEvent): Promise<void> {
    const target = e.target as HTMLElement;
    const teammateItem = target.closest('.teammate-item');
    
    if (!teammateItem) return;

    const userId = teammateItem.getAttribute('data-user-id');
    
    if (!userId) return;

    if (target.classList.contains('teammate-queue-button')) {
      this.handleQueueButton(userId);
    } else if (target.classList.contains('teammate-raid-link')) {
      e.preventDefault();
      const userName = target.getAttribute('data-user-name');
      if (userName) {
        await this.handleRaidLink(userId, userName, target);
      }
    }
  }

  private handleQueueButton(userId: string): void {
    if (raidQueueService.isQueued(userId)) {
      raidQueueService.removeFromQueue(userId);
    } else {
      const stream = this.teammateStreams.find(s => s.userId === userId);
      if (stream) {
        raidQueueService.addToQueue(stream);
      }
    }
    
    // Update the button state directly without re-rendering the entire list
    this.updateQueueButtonState(userId);
  }

  private updateQueueButtonState(userId: string): void {
    const teammateItem = this.teammatesList.querySelector(`[data-user-id="${userId}"]`);
    if (!teammateItem) return;

    const button = teammateItem.querySelector('.teammate-queue-button') as HTMLElement;
    if (!button) return;

    const isQueued = raidQueueService.isQueued(userId);
    
    // Update button appearance
    if (isQueued) {
      button.classList.add('queued');
      button.textContent = '−';
      button.title = 'Remove from queue';
      teammateItem.classList.add('queued');
    } else {
      button.classList.remove('queued');
      button.textContent = '+';
      button.title = 'Add to queue';
      teammateItem.classList.remove('queued');
    }

    // Re-sort the list to move queued teammates to the top
    this.sortTeammatesList();
  }

  private sortTeammatesList(): void {
    const items = Array.from(this.teammatesList.querySelectorAll('.teammate-item')) as HTMLElement[];
    
    // Sort items: queued first, then by username
    items.sort((a, b) => {
      const aQueued = a.classList.contains('queued');
      const bQueued = b.classList.contains('queued');
      
      // Queued teammates first
      if (aQueued && !bQueued) return -1;
      if (!aQueued && bQueued) return 1;
      
      // If both have same queue status, sort by name
      const aName = a.querySelector('.teammate-name')?.textContent || '';
      const bName = b.querySelector('.teammate-name')?.textContent || '';
      return aName.localeCompare(bName);
    });

    // Re-append items in sorted order
    items.forEach(item => this.teammatesList.appendChild(item));
  }

  private async updateTeammates(): Promise<void> {
    try {
      console.log('Updating teammates...');
      const teammateStreams = await this.fetchTeammateStreams();
      this.teammateStreams = teammateStreams;
      this.updateBadge();
      this.renderTeammates();
    } catch (error) {
      console.error('Error updating teammates:', error);
    }
  }

  private updateBadge(): void {
    const count = this.teammateStreams.length;
    this.badge.textContent = count.toString();
    this.badge.style.display = count > 0 ? 'flex' : 'none';
  }

  private renderTeammates(): void {
    if (this.teammateStreams.length === 0) {
      this.teammatesList.innerHTML = `
        <div class="empty-teammates">
          <span class="empty-teammates-icon">🤝</span>
          <span class="empty-teammates-text">No teammates online</span>
        </div>
      `;
      return;
    }

    // Sort teammates: queued ones first, then by name
    const sortedTeammates = [...this.teammateStreams].sort((a, b) => {
      const aQueued = raidQueueService.isQueued(a.userId);
      const bQueued = raidQueueService.isQueued(b.userId);
      
      // Queued teammates first
      if (aQueued && !bQueued) return -1;
      if (!aQueued && bQueued) return 1;
      
      // If both have same queue status, sort by name
      return a.userName.localeCompare(b.userName);
    });

    this.teammatesList.innerHTML = sortedTeammates.map(stream => {
      const isQueued = raidQueueService.isQueued(stream.userId);
      const teamsText = stream.teams && stream.teams.length > 0 
        ? stream.teams.map(team => team.team_name).join(', ')
        : '';

      return `
        <div class="teammate-item ${isQueued ? 'queued' : ''}" data-user-id="${stream.userId}">
          <div class="teammate-avatar">
            <img src="${stream.profileImageUrl}" alt="${stream.userName}">
          </div>
          <div class="teammate-info">
            <div class="teammate-name">${stream.userName}</div>
            <div class="teammate-title">${stream.title}</div>
            ${teamsText ? `<div class="teammate-teams">${teamsText}</div>` : ''}
            <div class="teammate-meta">
              <span class="teammate-viewers">👥 ${formatViewerCount(stream.viewerCount)}</span>
              <span class="teammate-duration">🕒 ${formatDuration(stream.startedAt)}</span>
              <a href="#" class="teammate-raid-link" data-user-id="${stream.userId}" data-user-name="${stream.userName}">raid now</a>
            </div>
          </div>
          <div class="teammate-actions">
            <button class="teammate-queue-button ${isQueued ? 'queued' : ''}" title="${isQueued ? 'Remove from queue' : 'Add to queue'}">
              ${isQueued ? '−' : '+'}
            </button>
          </div>
        </div>
      `;
    }).join('');
  }

  private async fetchTeammateStreams(): Promise<ExtendedStream[]> {
    try {
      const user = twitchService.getUser();
      console.log('Current Twitch user:', user);
      
      if (!user || !user.display_name) {
        console.log('No user or display name available for teammate fetch');
        return [];
      }

      console.log('Fetching teammates for user:', user.display_name);
      const teammatesResponse = await r3LabsService.getUserTeammates(user.display_name);
      console.log('R3D Labs teammates response:', teammatesResponse);
      
      if (!teammatesResponse.data || teammatesResponse.data.length === 0) {
        console.log('No teammates data or empty array');
        return [];
      }

      console.log(`Found ${teammatesResponse.data.length} teammates from R3D Labs (checking their live status...)`);

      // Create a map to store team information by user ID
      const teammateTeamsMap = new Map<string, Array<{team_id: string, team_name: string}>>();
      
      // Group teams by teammate user ID and collect unique user IDs
      const uniqueUserIds = new Set<string>();
      teammatesResponse.data.forEach(teammate => {
        uniqueUserIds.add(teammate.teammate_platform_user_id);
        
        const teams = teammateTeamsMap.get(teammate.teammate_platform_user_id) || [];
        teams.push({
          team_id: teammate.team_id,
          team_name: teammate.team_name
        });
        teammateTeamsMap.set(teammate.teammate_platform_user_id, teams);
      });

      const userIdArray = Array.from(uniqueUserIds);
      console.log(`Fetching Twitch stream details for ${userIdArray.length} unique teammates in batch...`);

      // Get all stream details in batch
      const streamDetailsList = await this.streamsService.getStreamsByUserIds(userIdArray);
      console.log(`Received ${streamDetailsList.length} live streams from batch request`);

      // Add team information to each stream
      const teammateStreams: ExtendedStream[] = streamDetailsList.map(streamDetails => {
        const extendedStream = {
          ...streamDetails,
          teams: teammateTeamsMap.get(streamDetails.userId) || []
        } as ExtendedStream;
        
        console.log(`Added teammate stream: ${streamDetails.userName} with teams:`, extendedStream.teams);
        return extendedStream;
      });

      console.log(`Final teammate streams count: ${teammateStreams.length}`);
      return teammateStreams;
    } catch (error) {
      console.error('Error fetching teammate streams:', error);
      return [];
    }
  }

  public async initialize(): Promise<() => void> {
    try {
      // Initialize TwitchStreamsService here instead of constructor
      const accessToken = twitchService.getAccessToken();
      if (!accessToken) {
        console.warn('TeammatesComponent: No access token available, skipping initialization');
        return () => {}; // Return empty cleanup function
      }

      this.streamsService = new TwitchStreamsService(
        import.meta.env.VITE_TWITCH_CLIENT_ID,
        accessToken
      );

      // Initialize elements first
      this.initializeElements();
      this.initializeEventListeners();
      
      // Initial load - wrap in try/catch to prevent errors from breaking app
      try {
        await this.updateTeammates();
      } catch (error) {
        console.error('TeammatesComponent: Failed to load initial teammates data:', error);
        // Don't throw - just log and continue
      }
      
      // Set up periodic refresh
      this.refreshInterval = window.setInterval(() => {
        this.updateTeammates().catch(error => {
          console.error('TeammatesComponent: Periodic refresh failed:', error);
        });
      }, this.REFRESH_INTERVAL);
      
      // Add visibility change listener to refresh when document becomes visible
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.updateTeammates().catch(error => {
            console.error('TeammatesComponent: Visibility refresh failed:', error);
          });
        }
      });

      // Return cleanup function
      return () => {
        if (this.refreshInterval) {
          clearInterval(this.refreshInterval);
          this.refreshInterval = null;
        }
      };
    } catch (error) {
      console.error('TeammatesComponent: Initialization failed:', error);
      // Return empty cleanup function instead of throwing
      return () => {};
    }
  }

  // Public method to get current teammate count for external use
  public getTeammateCount(): number {
    return this.teammateStreams.length;
  }

  // Public method to refresh teammates data
  public async refresh(): Promise<void> {
    await this.updateTeammates();
  }

  private async handleRaidLink(userId: string, userName: string, target: HTMLElement): Promise<void> {
    const result = await Swal.fire({
      title: 'Start Raid?',
      html: `Are you sure you want to raid <strong>${userName}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Start Raid!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#9147ff',
      cancelButtonColor: '#6e7681',
      background: '#18181b',
      color: '#ffffff',
      width: '360px'
    });

    if (result.isConfirmed) {
      try {
        const originalText = target.textContent;
        target.textContent = 'starting...';
        target.style.pointerEvents = 'none';
        
        await this.streamsService.raidChannel(userId);
        
        await Swal.fire({
          title: 'Raid Started!',
          html: `Successfully started raid to <strong>${userName}</strong>`,
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          confirmButtonColor: '#9147ff',
          background: '#18181b',
          color: '#ffffff',
          width: '360px'
        });

        setTimeout(() => {
          target.textContent = originalText;
          target.style.pointerEvents = 'auto';
        }, 5000);
      } catch (error) {
        console.error('Failed to start raid:', error);
        
        const errorMessage = error instanceof Error ? error.message : 'Unable to start the raid. Please try again.';
        await Swal.fire({
          title: 'Unable to Start Raid',
          html: `<div style="text-align: left; margin-top: 1rem;">${errorMessage}</div>`,
          icon: 'error',
          confirmButtonColor: '#9147ff',
          background: '#18181b',
          color: '#ffffff',
          width: '360px'
        });

        target.textContent = 'raid now';
        target.style.pointerEvents = 'auto';
      }
    }
  }
} 